/**
 * Router — path-based multi-tenant URL model.
 *
 * URL shape:
 *   /                              → redirect to user's home (or /login)
 *   /login                         → global / platform-default login
 *   /<tenant>/login                → branded login for that tenant
 *   /<tenant>/                     → home (MainPage)
 *   /<tenant>/chat, /agents, /admin, /insights, /settings, /logs, /flows…
 *   /super-admin/…                 → super-admin, tenant-less
 *   /design-system                 → super-admin
 *   /terms, /privacy               → public
 *   /google-access                 → Google consent popup landing (public)
 *
 * Every tenant-scoped route is nested under `/:tenantSlug/*` and
 * wrapped in <TenantBoundary />. The boundary is the structural lock:
 * <Outlet /> only mounts when the URL slug matches the user's
 * `company_tenant_code` (or the user is super_admin). There is no
 * useEffect-based guard, no listener race, no URL re-stamping — the
 * wrong-tenant render is unreachable by construction.
 *
 * Reserved top-level slugs (`login`, `super-admin`, `design-system`,
 * `terms`, `privacy`, `api`, `health`, `static`, `assets`, `_t`, `_`)
 * MUST be declared as routes BEFORE the catch-all `/:tenantSlug/*`
 * so React Router matches them first. The backend's tenant validator
 * (`backend/shared/constants/reserved_slugs.py`) prevents any tenant
 * from being created with one of these codes in the first place.
 */

import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '@modules/auth';
import { useFeature } from '@/hooks/useFeature';
import { useLanguage } from '@/hooks/useLanguage';
import { RequireFeature } from '@/components/RequireFeature';
import { flowsRoutes } from '@modules/flows';
import { theme } from 'antd';

// Pages
import { MainPage } from '@modules/dashboard';
import { ChatPage } from '@modules/conversations';
import { ConciergePage } from '@modules/concierge/pages/ConciergePage';
import { LearnPage } from '@modules/journey/pages/LearnPage';
import { BotsPage } from '@modules/chatbots';
import { AdminPage, AdminUsersPage, UserSettingsPage, LogDashboardPage } from '@modules/admin';
import { SuperAdminPage } from '@modules/super_admin';
import DesignSystemPage from '@modules/super_admin/design_system/DesignSystemPage';
import WhiteLabelEditorPage from '@modules/super_admin/design_system/WhiteLabelEditorPage';
import TenantWorkspacePage from '@modules/super_admin/pages/TenantWorkspacePage';
import ToolActivityPage from '@modules/observability/pages/ToolActivityPage';
import { PublicLegalPage, PUBLIC_DOCUMENTS_PUBLISHED, PUBLIC_LEGAL_PAGES, TermsAndPrivacyPage } from '@modules/legal';
import { GoogleAccessDonePage } from '@modules/cloud-storage/pages/GoogleAccessDonePage';
import { GOOGLE_ACCESS_PATH } from '@modules/cloud-storage/paths';
import { CONNECTOR_CONNECTED_PATH, MONDAY_CONNECTED_PATH } from '@/routing/popupLandings';
import { ConnectorConnectedPage } from '@modules/connector_library/components/ConnectorConnectedPage';
import { MondayConnectedPage } from '@modules/super_admin/design_system/MondayConnectedPage';
import { SkillsManagementPage } from '@modules/skills';
import { DataBundlesPage } from '@modules/data-bundles';
import { VaultPage } from '@modules/vault';
import { InsightsDashboardPage } from '@modules/insights';
import { AgentOfAgentsPage } from '@modules/agent-of-agents';
import { ProjectsListPage, ProjectPage } from '@modules/projects';
import { NotificationBell } from '@modules/projects/components/NotificationBell';
import { CostSimulatorPage } from '@modules/cost_simulator';
import { ForecastAccuracyPage } from '@modules/cost_simulator/pages/ForecastAccuracyPage';
import { LoadingScreen } from '@/components/common/LoadingScreen';

// Routing primitives — the new path-based model
import { TenantBoundary, PublicTenantLogin, PublicTenantResetPassword, BrandResolver, VanityRouter, buildUserHomePath, loginPathForHost, DEFAULT_LOGIN_PATH } from './routing';

/**
 * Role gate for routes that need stronger access than "any signed-in
 * user". TenantBoundary already established the user is authenticated
 * AND inside their own tenant — this only checks role hierarchy.
 */
interface RoleGateProps {
  children: React.ReactNode;
  requireRole: 'super_admin' | 'group_admin' | 'admin' | 'user';
}
function RoleGate({ children, requireRole }: RoleGateProps) {
  const { user, hasRole } = useAuth();
  if (!user) return <LoadingScreen />;
  if (!hasRole(requireRole)) return <Navigate to={buildUserHomePath(user)} replace />;
  return <>{children}</>;
}

/**
 * SuperAdminTenantGate — enforces that super-admin / design-system pages
 * render ONLY on the super_admin's own home tenant URL. If a super_admin
 * lands on `/<other-tenant>/super-admin`, redirect to
 * `/<their-home-tenant>/super-admin`. Privacy rule: super-admin tooling
 * must show under the super_admin's own brand, never under a customer's.
 *
 * Wrapped INSIDE RoleGate, so by the time we run here the role check has
 * passed and `user` is non-null. We just need the slug check.
 */
function SuperAdminTenantGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { tenantSlug = '' } = useParams<{ tenantSlug?: string }>();
  const slug = tenantSlug.toLowerCase();
  const homeSlug = (user?.company_tenant_code || user?.tenant_code || '').toLowerCase();
  if (homeSlug && slug !== homeSlug) {
    // Preserve the rest of the path (e.g. /tenants/X/brand)
    const tail = window.location.pathname.replace(/^\/[^/]+/, '');
    return <Navigate to={`/${homeSlug}${tail}`} replace />;
  }
  return <>{children}</>;
}

/**
 * Top-level shortcut: routes like `/admin`, `/agents`, `/flows`,
 * `/insights`, `/cost-simulator`, `/logs`, `/chat` aren't real
 * pages — they redirect to the SAME page under the user's tenant
 * (`/<user.company_tenant_code>/<page>`).
 *
 * Without these shortcuts, typing `/admin` in the URL bar would
 * match the `/:tenantSlug` route with `tenantSlug='admin'`, mount
 * MainPage thinking the active tenant is 'admin', and every
 * subsequent sidebar click would build `/admin/agents`, `/admin/flows`
 * — the "admin sticks to next page" bug.
 */
function PageShortcutRedirect({ page }: { page: string }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  // loginPathForHost() (not DEFAULT_LOGIN_PATH) so an unauthenticated
  // hit here on a vanity host can never emit/expose the platform slug.
  if (!isAuthenticated) return <Navigate to={loginPathForHost()} replace />;
  const slug = (user?.company_tenant_code || user?.tenant_code || '').toLowerCase();
  if (!slug) return <Navigate to={loginPathForHost()} replace />;
  // Carry the query string across: the OAuth round trip's `?state=…&code=…`
  // is what Settings finishes the connection with (item 66).
  const search = typeof window !== 'undefined' ? window.location.search : '';
  return <Navigate to={`/${slug}${page}${search}`} replace />;
}

/**
 * Top-level redirect for `/`. Sends authenticated users to their home
 * tenant URL (or /super-admin for super_admin), unauthenticated users
 * to the global /login.
 */
function RootRedirect() {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  // loginPathForHost() resolves to the vanity host's own tenant login
  // when on a custom domain, so the catch-all -> "/" -> here path can
  // never fall back to (and leak) the platform-default tenant slug.
  if (!isAuthenticated) return <Navigate to={loginPathForHost()} replace />;
  return <Navigate to={buildUserHomePath(user)} replace />;
}

/**
 * HomeFallback — the shared fallback for the home route (`modules.agents` OFF)
 * AND for module routes like `/concierge` (where `modules.agents` may well be
 * ON). Resolution order: if the user has the Agents home, send them there; else
 * if Flows is on, go to /<tenant>/flows; otherwise show the empty state asking
 * the admin to enable a module.
 *
 * The Agents check matters for the module-route case: without it, a user who
 * simply lacks (say) `modules.concierge` but has Agents on would be shown a
 * misleading "no modules enabled" screen instead of their actual home.
 */
function HomeFallback() {
  const agentsEnabled = useFeature('modules.agents');
  const flowsEnabled = useFeature('modules.flows');
  const { tenantSlug } = useParams();
  const { t } = useLanguage();
  const { token } = theme.useToken();
  // Agents home exists → land there. (At the home index route agents is OFF by
  // definition — that's why this fallback rendered — so this never loops.)
  if (agentsEnabled && tenantSlug) return <Navigate to={`/${tenantSlug}`} replace />;
  // Tenant slug is available here via TenantBoundary's parent route
  // params, but the simpler "navigate to flows" rendered as a relative
  // link works because we're already nested under /:tenantSlug/.
  if (flowsEnabled) return <Navigate to="flows" replace />;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', textAlign: 'center', padding: token.paddingXL }}>
      <div style={{ maxWidth: token.screenSM }}>
        <h2 style={{ marginBlockEnd: token.marginXS }}>{t('home.noModulesTitle')}</h2>
        <p style={{ opacity: 0.7 }}>
          {t('home.noModulesBody')}
        </p>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* ── Public / tenant-less routes (declared FIRST so they
            match before /:tenantSlug/*) ────────────────────────── */}

      {/* Root — redirects based on auth state. Anonymous users land
          on the platform-owner tenant's branded login. */}
      <Route path="/" element={<RootRedirect />} />

      {/* Legacy tenant-less /login → platform default branded login.
          Privacy rule: there is no generic, tenant-less login UI —
          every login renders the brand of the tenant in the URL. */}
      <Route path="/login" element={<Navigate to={DEFAULT_LOGIN_PATH} replace />} />

      {/* The platform's public documents — the same on every host, no login,
          no tenant brand. Prerendered to static HTML at build (see
          modules/legal/publicPaths.ts); these routes are the live copy. */}
      {PUBLIC_DOCUMENTS_PUBLISHED && PUBLIC_LEGAL_PAGES.map((page) => (
        <Route key={page} path={`/${page}`} element={<PublicLegalPage page={page} />} />
      ))}
      {PUBLIC_DOCUMENTS_PUBLISHED && <Route path="/legal" element={<Navigate to="/terms" replace />} />}

      {/* Where a Google consent popup lands: it tells the opener and closes.
          No session and no tenant — under /<tenant>/ the popup would meet
          the login gate (modules/cloud-storage/paths.ts). */}
      <Route path={GOOGLE_ACCESS_PATH} element={<GoogleAccessDonePage />} />
      {/* Where the "Connect monday.com" popup lands (White-Label → Support). */}
      <Route path={MONDAY_CONNECTED_PATH} element={<MondayConnectedPage />} />
      {/* Where a person's own sign-in to a connector lands (item 65A). */}
      <Route path={CONNECTOR_CONNECTED_PATH} element={<ConnectorConnectedPage />} />

      {/* Top-level page shortcuts — typing /admin, /super-admin,
          /design-system, /settings, etc. in the URL bar redirects to
          /<your-tenant>/<page>. Privacy rule: an authenticated user
          must NEVER see UI without their tenant slug in the URL (any
          drift would expose another tenant's brand/UI). See
          PageShortcutRedirect for why this matters. */}
      <Route path="/admin" element={<PageShortcutRedirect page="/admin" />} />
      <Route path="/admin/*" element={<PageShortcutRedirect page="/admin" />} />
      <Route path="/agents" element={<PageShortcutRedirect page="/agents" />} />
      {/* Renamed 2026-09-07: /bots -> /agents, /vault -> /library. The old
          shortcuts stay as redirects so bookmarks and muscle memory land on
          the new page instead of a 404. */}
      <Route path="/bots" element={<PageShortcutRedirect page="/agents" />} />
      <Route path="/chat" element={<PageShortcutRedirect page="/chat" />} />
      <Route path="/flows" element={<PageShortcutRedirect page="/flows" />} />
      <Route path="/flows/*" element={<PageShortcutRedirect page="/flows" />} />
      <Route path="/skills" element={<PageShortcutRedirect page="/skills" />} />
      <Route path="/data-bundles" element={<PageShortcutRedirect page="/data-bundles" />} />
      <Route path="/library" element={<PageShortcutRedirect page="/library" />} />
      <Route path="/vault" element={<PageShortcutRedirect page="/library" />} />
      <Route path="/insights" element={<PageShortcutRedirect page="/insights" />} />
      <Route path="/projects" element={<PageShortcutRedirect page="/projects" />} />
      <Route path="/projects/*" element={<PageShortcutRedirect page="/projects" />} />
      <Route path="/agent-of-agents" element={<PageShortcutRedirect page="/agent-of-agents" />} />
      <Route path="/cost-simulator" element={<PageShortcutRedirect page="/cost-simulator" />} />
      <Route path="/logs" element={<PageShortcutRedirect page="/logs" />} />
      <Route path="/settings" element={<PageShortcutRedirect page="/settings" />} />
      <Route path="/super-admin" element={<PageShortcutRedirect page="/super-admin" />} />
      <Route path="/super-admin/*" element={<PageShortcutRedirect page="/super-admin" />} />
      <Route path="/design-system" element={<PageShortcutRedirect page="/design-system" />} />

      {/* ── Tenant-scoped routes ─────────────────────────────────── */}
      <Route path="/:tenantSlug" element={<TenantBoundary />}>

        {/* Home */}
        <Route
          index
          element={
            <RequireFeature flag="modules.agents" fallback={<HomeFallback />}>
              <MainPage />
            </RequireFeature>
          }
        />

        {/* Agents experience */}
        <Route
          path="chat"
          element={
            <RequireFeature flag="modules.agents" fallback={<HomeFallback />}>
              <ChatPage />
            </RequireFeature>
          }
        />
        <Route
          path="chat/:conversationId"
          element={
            <RequireFeature flag="modules.agents" fallback={<HomeFallback />}>
              <ChatPage />
            </RequireFeature>
          }
        />

        {/* Concierge — full-page front door (same shell as the chat page). */}
        <Route
          path="concierge"
          element={
            <RequireFeature flag="modules.concierge" fallback={<HomeFallback />}>
              <ConciergePage />
            </RequireFeature>
          }
        />
        {/* Help & tutorials — the library, the player and the Guide. Exists
            only where the guided onboarding is switched on for the tenant. */}
        <Route
          path="learn"
          element={
            <RequireFeature flag="modules.journey" fallback={<HomeFallback />}>
              <LearnPage />
            </RequireFeature>
          }
        />
        <Route
          path="agents"
          element={
            <RequireFeature flag="modules.agents" fallback={<HomeFallback />}>
              <BotsPage />
            </RequireFeature>
          }
        />

        {/* Admin page + flat sub-pages.
            Skills and Data Bundles USED to live at /admin/skills and
            /admin/data-bundles. Flattened to /skills and /data-bundles
            because they're independent pages, not children of admin
            — the nesting was a UX trap ("admin sticks to the URL of
            the next page"). /admin/users stays nested because Users
            is genuinely an admin-only sub-page. Backward-compat
            redirects below catch any stale /admin/skills or
            /admin/data-bundles bookmarks. */}
        <Route path="admin" element={<RoleGate requireRole="admin"><AdminPage /></RoleGate>} />
        <Route path="admin/users" element={<RoleGate requireRole="admin"><AdminUsersPage /></RoleGate>} />
        <Route
          path="skills"
          element={
            <RoleGate requireRole="admin">
              <RequireFeature flag="modules.agents" fallback={<HomeFallback />}>
                <SkillsManagementPage />
              </RequireFeature>
            </RoleGate>
          }
        />
        <Route
          path="data-bundles"
          element={
            <RoleGate requireRole="admin">
              <RequireFeature flag="modules.agents" fallback={<HomeFallback />}>
                {/* Stays reachable on the Library too. The bundles are a
                    read-only projection there, and the page says so — but a
                    person who kept the URL should still find their bundles
                    where they left them, not be bounced to the home page.
                    The tab inside /agents shows the same page for the same
                    reason; this route must not disagree with it. */}
                <DataBundlesPage />
              </RequireFeature>
            </RoleGate>
          }
        />
        {/* The Library — nested folder file system (replaces Data Bundles).
            Gated per tenant on modules.library, toggled from the super-admin
            features editor like any other module. Within an enabled tenant it
            administrators run it, and everyone else reaches it only once
            something has actually been shared with them (operator decision
            2026-08-14) — until then they have no Library, exactly as they have
            no Data Bundles. Per-node rights gate every action server-side on
            top of that. */}
        <Route
          path="library"
          element={
            <RequireFeature flag="modules.library" fallback={<HomeFallback />}>
              {/* Admin roles only (operator decision 2026-08-24): a plain
                  user typing /library goes home, same as the hidden sidebar
                  entry. The backend refuses the role as well. */}
              <RoleGate requireRole="admin">
                <VaultPage />
              </RoleGate>
            </RequireFeature>
          }
        />
        {/* Legacy redirects: old nested URLs → new flat ones. */}
        <Route path="admin/skills" element={<Navigate to="../skills" replace />} />
        <Route path="admin/data-bundles" element={<Navigate to="../data-bundles" replace />} />
        {/* Renamed 2026-09-07: the Agents tab lives at /agents (was /bots)
            and the Library at /library (was /vault). */}
        <Route path="bots" element={<Navigate to="../agents" replace />} />
        <Route path="vault" element={<Navigate to="../library" replace />} />

        {/* Projects — shared multi-user workspaces. Open to every role (a plain
            `user` can create a project and invite colleagues in their own
            tenant), gated only on the per-tenant `modules.projects` flag. */}
        <Route
          path="projects"
          element={
            <RequireFeature flag="modules.projects" fallback={<HomeFallback />}>
              <ProjectsListPage />
            </RequireFeature>
          }
        />
        <Route
          path="projects/:projectId"
          element={
            <RequireFeature flag="modules.projects" fallback={<HomeFallback />}>
              <ProjectPage />
            </RequireFeature>
          }
        />

        {/* Insights — group_admin+ + feature flag */}
        <Route
          path="insights"
          element={
            <RoleGate requireRole="group_admin">
              <RequireFeature flag="modules.insights">
                <InsightsDashboardPage />
              </RequireFeature>
            </RoleGate>
          }
        />

        {/* Agent of Agents — group_admin+ + feature flag */}
        <Route
          path="agent-of-agents"
          element={
            <RoleGate requireRole="group_admin">
              <RequireFeature flag="modules.agent_of_agents">
                <AgentOfAgentsPage />
              </RequireFeature>
            </RoleGate>
          }
        />

        {/* Cost Simulator — group_admin+ + feature flag */}
        <Route
          path="cost-simulator"
          element={
            <RoleGate requireRole="group_admin">
              <RequireFeature flag="modules.cost_simulator">
                <CostSimulatorPage />
              </RequireFeature>
            </RoleGate>
          }
        />

        {/* Forecast accuracy — Phase 4 feedback loop showing predicted vs actual */}
        <Route
          path="cost-simulator/forecast-accuracy"
          element={
            <RoleGate requireRole="group_admin">
              <RequireFeature flag="modules.cost_simulator">
                <ForecastAccuracyPage />
              </RequireFeature>
            </RoleGate>
          }
        />

        {/* User settings */}
        <Route path="settings" element={<UserSettingsPage />} />
        {/* The original Terms of Service + Privacy Policy, for signed-in
            users from Settings → Legal (not public, not indexable). */}
        <Route path="settings/terms" element={<TermsAndPrivacyPage />} />

        {/* Logs */}
        <Route path="logs" element={<RoleGate requireRole="admin"><LogDashboardPage /></RoleGate>} />

        {/* Super-admin pages — pinned to the super_admin's own home
            tenant. SuperAdminTenantGate redirects to /<home>/super-admin
            when accessed on any other tenant's surface (no cross-tenant
            super-admin UI; their brand must show on their own URL). */}
        <Route
          path="super-admin"
          element={
            <RoleGate requireRole="super_admin">
              <SuperAdminTenantGate>
                <SuperAdminPage />
              </SuperAdminTenantGate>
            </RoleGate>
          }
        />
        {/* Tenant workspace — the ONE owner-screen for a tenant. The
            optional :section? param covers overview/modules/models/access/
            branding/integrations/activity; the static /brand and
            /tool-activity routes below outrank the dynamic segment. */}
        <Route
          path="super-admin/tenants/:tenantId/:section?"
          element={
            <RoleGate requireRole="super_admin">
              <SuperAdminTenantGate>
                <TenantWorkspacePage />
              </SuperAdminTenantGate>
            </RoleGate>
          }
        />
        <Route
          path="super-admin/tenants/:tenantId/brand"
          element={
            <RoleGate requireRole="super_admin">
              <SuperAdminTenantGate>
                <WhiteLabelEditorPage />
              </SuperAdminTenantGate>
            </RoleGate>
          }
        />
        <Route
          path="super-admin/tenants/:tenantId/tool-activity"
          element={
            <RoleGate requireRole="super_admin">
              <SuperAdminTenantGate>
                <ToolActivityPage />
              </SuperAdminTenantGate>
            </RoleGate>
          }
        />
        <Route
          path="design-system"
          element={
            <RoleGate requireRole="super_admin">
              <SuperAdminTenantGate>
                <DesignSystemPage />
              </SuperAdminTenantGate>
            </RoleGate>
          }
        />

        {/* Flows module — gated by modules.flows. The existing
            flowsRoutes export uses paths like '/flows/...'; we strip
            the leading '/' and nest. */}
        {flowsRoutes.map((route) => {
          const childPath = route.path.replace(/^\//, '');
          const flowEl = (
            <RequireFeature flag="modules.flows">
              <Suspense fallback={<LoadingScreen />}>
                <route.component />
              </Suspense>
            </RequireFeature>
          );
          return (
            <Route
              key={route.path}
              path={childPath}
              element={
                route.requireRole
                  ? <RoleGate requireRole={route.requireRole}>{flowEl}</RoleGate>
                  : flowEl
              }
            />
          );
        })}
      </Route>

      {/* Branded login lives OUTSIDE TenantBoundary so it can render
          to unauthenticated users. The brand still resolves from the
          URL slug via App.tsx's brand-resolution effect. */}
      <Route path="/:tenantSlug/login" element={<PublicTenantLogin />} />

      {/* Branded set-password page (reset + invitation links) — public
          for the same reason: recipients aren't authenticated yet. */}
      <Route path="/:tenantSlug/reset-password" element={<PublicTenantResetPassword />} />

      {/* Catch-all → root redirect (which sends to home or /login) */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function Router() {
  return (
    <VanityRouter>
      <BrandResolver />
      {/* App-wide chrome: fixed in the top inline-end corner on every
          authenticated page, so an invitation is reachable from wherever you
          are — including mid-conversation. Mounted HERE rather than in
          App.tsx because it navigates (useNavigate / useTenantPath) and so
          must sit inside the router; outside it, the whole app renders blank.
          Pages that own that corner clear it via --bell-corner-inline. */}
      <NotificationBell />
      <AppRoutes />
    </VanityRouter>
  );
}
